import { useEffect } from 'react';
import { salePrice, saleAppliesTo, saleEndsAt } from '../utils/sale';

const DOMAIN = 'https://rezvanifurniturestudio.com';
const BRAND = 'Rezvani Furniture Studio';
const DEFAULT_OG_IMAGE = `${DOMAIN}/og-default.jpg`;

export const SEO = ({ title, description, canonical, type = 'website', jsonLd, image, price, originalPrice }) => {
  useEffect(() => {
    // Title tag (50-60 chars)
    document.title = title ? `${title} - ${BRAND}` : `${BRAND} | Premium In-Pool Furniture by Tenjam`;

    // Meta description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc && description) {
      metaDesc.setAttribute('content', description);
    }

    // Canonical
    let canonicalEl = document.querySelector('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', canonical || window.location.href);

    const shareImage = image || DEFAULT_OG_IMAGE;
    const shareUrl = canonical || window.location.href;

    // Open Graph tags
    setMeta('og:title', title ? `${title} - ${BRAND}` : BRAND);
    setMeta('og:description', description || '');
    setMeta('og:type', type);
    setMeta('og:url', shareUrl);
    setMeta('og:site_name', BRAND);
    setMeta('og:image', shareImage);
    setMeta('og:image:alt', title || BRAND);

    // Product price (only meaningful for product pages). `price` is the price
    // the shopper pays right now (sale price while a sale is live).
    if (type === 'product' && typeof price === 'number') {
      setMeta('product:price:amount', String(price));
      setMeta('product:price:currency', 'USD');
      if (typeof originalPrice === 'number' && originalPrice > price) {
        setMeta('product:original_price:amount', String(originalPrice));
        setMeta('product:original_price:currency', 'USD');
      } else {
        removeMeta('product:original_price:amount');
        removeMeta('product:original_price:currency');
      }
    }

    // Twitter Card tags (name-based)
    setNameMeta('twitter:card', 'summary_large_image');
    setNameMeta('twitter:title', title ? `${title} - ${BRAND}` : BRAND);
    setNameMeta('twitter:description', description || '');
    setNameMeta('twitter:image', shareImage);

    // JSON-LD
    const existingScripts = document.querySelectorAll('script[data-seo-jsonld]');
    existingScripts.forEach(s => s.remove());
    if (jsonLd) {
      const ldArray = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      ldArray.forEach(ld => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-jsonld', 'true');
        script.textContent = JSON.stringify(ld);
        document.head.appendChild(script);
      });
    }

    return () => {
      const scripts = document.querySelectorAll('script[data-seo-jsonld]');
      scripts.forEach(s => s.remove());
    };
  }, [title, description, canonical, type, jsonLd, image, price, originalPrice]);

  return null;
};

function setMeta(property, content) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(property) {
  document.querySelectorAll(`meta[property="${property}"]`).forEach((el) => el.remove());
}

function setNameMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

// JSON-LD generators
export const organizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: BRAND,
  url: DOMAIN,
  logo: `${DOMAIN}/logo.png`,
  description: 'Premium in-pool furniture dealer featuring Tenjam SPLASH Series loungers, chairs, tables and stools for residential and commercial pools.',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '511 N Entrada St',
    addressLocality: 'Chandler',
    addressRegion: 'AZ',
    postalCode: '85226',
    addressCountry: 'US'
  },
  telephone: '+1-623-888-8644',
  sameAs: []
});

// Offer price must match what the shopper pays right now (Google Merchant
// Center's crawler reads this and overrides manual sale prices on mismatch).
// While a sale is live: `price` = sale price, original shown as StrikethroughPrice.
export const productSchema = (product, sale) => {
  const onSale = saleAppliesTo(sale, product);
  const current = onSale ? salePrice(product.price, sale, product) : product.price;
  const endsAt = onSale ? saleEndsAt(sale, product) : null;
  const offer = {
    '@type': 'Offer',
    url: `${DOMAIN}/products/${product.category}/${product.slug}`,
    priceCurrency: 'USD',
    price: Number(current).toFixed(2),
    availability: product.inStock !== false ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
    itemCondition: 'https://schema.org/NewCondition',
    seller: { '@type': 'Organization', name: BRAND }
  };
  if (onSale) {
    offer.priceValidUntil = endsAt ? endsAt.slice(0, 10) : undefined;
    offer.priceSpecification = [
      {
        '@type': 'UnitPriceSpecification',
        price: Number(current).toFixed(2),
        priceCurrency: 'USD',
        ...(endsAt ? { validThrough: endsAt } : {})
      },
      {
        '@type': 'UnitPriceSpecification',
        priceType: 'https://schema.org/StrikethroughPrice',
        price: Number(product.price).toFixed(2),
        priceCurrency: 'USD'
      }
    ];
  }
  return {
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: product.name,
  description: product.description,
  image: product.image,
  sku: product.sku || product.id,
  brand: { '@type': 'Brand', name: 'Tenjam' },
  offers: offer,
  aggregateRating: product.reviews > 0 ? {
    '@type': 'AggregateRating',
    ratingValue: product.rating,
    reviewCount: product.reviews
  } : undefined
  };
};

export const breadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.name,
    item: item.url ? `${DOMAIN}${item.url}` : undefined
  }))
});

export const faqSchema = (questions) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: questions.map(q => ({
    '@type': 'Question',
    name: q.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: q.answer
    }
  }))
});

export { DOMAIN, BRAND };
